import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { MdOutlineVisibility } from "react-icons/md";
import { toast } from "react-toastify";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  sortableKeyboardCoordinates,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import {
  FulfillmentMerchant,
  FulfillmentMerchantCode,
  FulfillmentPriorityShop,
} from "../../../../@types/FulfillmentPriority";
import { ConfirmationModal } from "../../../../components/ConfirmationModal";
import { Button } from "../../../../components/New/Button";
import { Loading } from "../../../../components/New/Loading";
import { NoDataAvailableImage } from "../../../../components/NoDataAvailableImage";
import { PageHeader } from "../../../../components/Headers/PageHeader";
import { SwitchButton } from "../../../../components/Switch";
import { useFulfillmentPriorityAccess } from "../../../../hooks/useFulfillmentPriorityAccess";
import { useToast } from "../../../../hooks/useToast";
import { useLeavePageConfirm } from "../../../../shared/HasUnsavedChanges";
import {
  getFulfillmentMerchants,
  getFulfillmentPriorityShops,
  updateFulfillmentPriorityShops,
} from "../../../../services/fulfillmentPriority.service";
import {
  buildFulfillmentPriorityPayload,
  canSaveFulfillmentPriority,
  getFulfillmentMerchantLabel,
  getDefaultFulfillmentMerchantCode,
  getFulfillmentPriorityShopKey,
  haveFulfillmentPriorityShopsChanged,
  normalizeFulfillmentMerchants,
  normalizeFulfillmentPriorityShops,
  reorderFulfillmentPriorityShops,
} from "../../../../shared/FulfillmentPriority";
import { FulfillmentPriorityErrorToast } from "./components/FulfillmentPriorityErrorToast";
import { ShopCard } from "./components/ShopCard";
import { SortableShopCard } from "./components/SortableShopCard";

import styles from "./FulfillmentPriority.module.css";

export function FulfillmentPriority() {
  const { t } = useTranslation();
  const { toastify } = useToast();
  const { isReadOnly } = useFulfillmentPriorityAccess();
  const [merchants, setMerchants] = useState<FulfillmentMerchant[]>([]);
  const [merchantCode, setMerchantCode] = useState<FulfillmentMerchantCode>("");
  const [pendingMerchantCode, setPendingMerchantCode] =
    useState<FulfillmentMerchantCode>("");
  const [shops, setShops] = useState<FulfillmentPriorityShop[]>([]);
  const [originalShops, setOriginalShops] = useState<FulfillmentPriorityShop[]>(
    [],
  );
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [pendingShowInactive, setPendingShowInactive] = useState<
    boolean | null
  >(null);
  const previousShopQuery = useRef({
    merchantCode: "",
    includeInactive: false,
  });
  const loadRequestId = useRef(0);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const hasPendingChanges = useMemo(
    () => haveFulfillmentPriorityShopsChanged(originalShops, shops),
    [originalShops, shops],
  );

  useLeavePageConfirm(hasPendingChanges);

  const loadMerchants = useCallback(async () => {
    const requestId = ++loadRequestId.current;
    setLoading(true);
    setLoadError(false);

    try {
      const response = await getFulfillmentMerchants();
      if (requestId !== loadRequestId.current) return;

      const nextMerchants = normalizeFulfillmentMerchants(response);
      setMerchants(nextMerchants);
      setShops([]);
      setOriginalShops([]);
      setMerchantCode((selectedMerchant) =>
        nextMerchants.some(
          (merchant) => merchant.merchantCode === selectedMerchant,
        )
          ? selectedMerchant
          : getDefaultFulfillmentMerchantCode(nextMerchants),
      );

      if (nextMerchants.length === 0) setLoading(false);
    } catch (error) {
      console.error("Error loading fulfillment merchants:", error);
      if (requestId === loadRequestId.current) {
        setMerchants([]);
        setMerchantCode("");
        setShops([]);
        setOriginalShops([]);
        setLoadError(true);
        setLoading(false);
      }
    }
  }, []);

  const loadShops = useCallback(
    async (
      selectedMerchant: FulfillmentMerchantCode,
      includeInactive: boolean,
      preserveCurrentList = false,
    ) => {
      const requestId = ++loadRequestId.current;
      if (preserveCurrentList) {
        setFilterLoading(true);
      } else {
        setLoading(true);
        setFilterLoading(false);
      }
      setLoadError(false);

      try {
        const response = await getFulfillmentPriorityShops(selectedMerchant, {
          includeInactive,
        });
        if (requestId === loadRequestId.current) {
          const nextShops = normalizeFulfillmentPriorityShops(response);
          setShops(nextShops);
          setOriginalShops(nextShops);
        }
      } catch (error) {
        console.error("Error loading fulfillment priority shops:", error);
        if (requestId === loadRequestId.current) {
          if (!preserveCurrentList) {
            setShops([]);
            setOriginalShops([]);
          }
          setLoadError(true);
        }
      } finally {
        if (requestId === loadRequestId.current) {
          if (preserveCurrentList) {
            setFilterLoading(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    [],
  );

  useEffect(() => {
    loadMerchants();
    return () => {
      loadRequestId.current += 1;
    };
  }, [loadMerchants]);

  useEffect(() => {
    if (!merchantCode) return;

    const previousQuery = previousShopQuery.current;
    const isFilterRefetch =
      previousQuery.merchantCode === merchantCode &&
      previousQuery.includeInactive !== showInactive;
    previousShopQuery.current = {
      merchantCode,
      includeInactive: showInactive,
    };
    loadShops(merchantCode, showInactive, isFilterRefetch);
  }, [loadShops, merchantCode, showInactive]);

  const handleSave = async () => {
    if (
      !canSaveFulfillmentPriority({
        isReadOnly,
        hasPendingChanges,
        saving,
        filterLoading,
      })
    ) {
      return;
    }

    const updateToastId = toast.loading(t("fulfillmentPriorityUpdating"), {
      position: "top-center",
      closeOnClick: false,
      pauseOnHover: false,
      draggable: false,
    });
    setSaving(true);

    try {
      await updateFulfillmentPriorityShops(
        buildFulfillmentPriorityPayload(shops, merchantCode),
      );
      setOriginalShops(shops);
      toast.update(updateToastId, {
        render: t("fulfillmentPriorityUpdated"),
        type: "success",
        isLoading: false,
        autoClose: 3500,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      try {
        const response = await getFulfillmentPriorityShops(merchantCode, {
          includeInactive: showInactive,
        });
        const refreshedShops = normalizeFulfillmentPriorityShops(response);
        setShops(refreshedShops);
        setOriginalShops(refreshedShops);
      } catch (error) {
        console.error(
          "Error refreshing fulfillment priority shops after update:",
          error,
        );
        toastify("error", t("fulfillmentPriorityLoadError"), "top-center");
      }
    } catch (error) {
      console.error("Error updating fulfillment priority shops:", error);
      const responseData = axios.isAxiosError(error)
        ? error.response?.data
        : undefined;
      const backendMessage =
        responseData &&
        typeof responseData === "object" &&
        "message" in responseData
          ? String(responseData.message)
          : "";
      const errorMessage: string =
        backendMessage.trim() || String(t("fulfillmentPriorityUpdateError"));
      const errorMessages = errorMessage
        .split(/\r?\n/)
        .map((message) => message.trim())
        .filter(Boolean);
      const [summary, ...details] = errorMessages;
      const hasDetails = details.length > 0;

      toast.update(updateToastId, {
        render: hasDetails ? (
          <FulfillmentPriorityErrorToast
            summary={summary}
            details={details}
            viewDetailsLabel={t("fulfillmentPriorityViewErrorDetails", {
              count: details.length,
            })}
            hideDetailsLabel={t("fulfillmentPriorityHideErrorDetails")}
            detailsLabel={t("fulfillmentPriorityErrorDetails")}
          />
        ) : (
          summary
        ),
        type: "error",
        isLoading: false,
        autoClose: hasDetails ? false : 5000,
        closeButton: true,
        closeOnClick: !hasDetails,
        pauseOnHover: true,
        draggable: !hasDetails,
        hideProgressBar: hasDetails,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleMerchantChange = (nextMerchantCode: FulfillmentMerchantCode) => {
    if (nextMerchantCode === merchantCode) return;

    if (hasPendingChanges) {
      setPendingMerchantCode(nextMerchantCode);
      return;
    }

    setMerchantCode(nextMerchantCode);
  };

  const confirmPendingChange = () => {
    if (pendingShowInactive !== null) {
      setShops(originalShops);
      setShowInactive(pendingShowInactive);
      setPendingShowInactive(null);
      return;
    }

    setMerchantCode(pendingMerchantCode);
    setPendingMerchantCode("");
  };

  const handleShowInactiveChange = (nextValue: boolean) => {
    if (
      nextValue === showInactive ||
      !merchantCode ||
      saving ||
      loading ||
      filterLoading
    ) {
      return;
    }

    if (hasPendingChanges) {
      setPendingShowInactive(nextValue);
      return;
    }

    setShowInactive(nextValue);
  };

  const handleStatusChange = (shopToChange: FulfillmentPriorityShop) => {
    if (saving || filterLoading) return;

    const updatedShop = {
      ...shopToChange,
      isActive: !shopToChange.isActive,
    };
    setShops((currentShops) =>
      currentShops.map((shop) =>
        getFulfillmentPriorityShopKey(shop) ===
        getFulfillmentPriorityShopKey(shopToChange)
          ? updatedShop
          : shop,
      ),
    );
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (saving || filterLoading || !over || active.id === over.id) return;

    const nextShops = reorderFulfillmentPriorityShops(
      shops,
      String(active.id),
      String(over.id),
      !showInactive,
    );

    if (nextShops === shops) return;
    setShops(nextShops);
  };

  const handleReset = () => {
    if (!hasPendingChanges || saving || filterLoading) return;

    setShops(originalShops);
  };

  const shopKeys = useMemo(
    () => shops.map(getFulfillmentPriorityShopKey),
    [shops],
  );
  const isLoadingShopList = loading || filterLoading;
  const hasVisibleShopList =
    !isLoadingShopList && !loadError && shops.length > 0;

  return (
    <main className={styles.container}>
      <PageHeader
        title={t("fulfillmentPriority")}
        description={t("fulfillmentPriorityPageDescription")}
      />

      <section className={styles.content}>
        {isReadOnly && (
          <div className={styles.readOnlyNotice} role="status">
            <MdOutlineVisibility aria-hidden="true" />
            <p>{t("fulfillmentPriorityReadOnlyNotice")}</p>
          </div>
        )}

        <section className={styles.merchantSelector}>
          <div className={styles.merchantSelectorHeader}>
            <div
              className={styles.merchantTabs}
              role="tablist"
              aria-label={t("selectMerchantPriorityList")}
            >
              {merchants.map((merchant) => (
                <button
                  className={`${styles.merchantTab} ${
                    merchantCode === merchant.merchantCode ? styles.active : ""
                  }`}
                  key={merchant.merchantCode}
                  type="button"
                  role="tab"
                  aria-selected={merchantCode === merchant.merchantCode}
                  disabled={saving || filterLoading}
                  onClick={() => handleMerchantChange(merchant.merchantCode)}
                >
                  {getFulfillmentMerchantLabel(
                    merchant,
                    t("ffsFulfillmentFromStore"),
                  )}
                </button>
              ))}
            </div>

            <div className={styles.showInactiveRow}>
              <div className={styles.showInactiveControl}>
                <SwitchButton
                  ariaLabel={t("showInactive")}
                  checked={showInactive}
                  disabled={saving || loading || filterLoading || !merchantCode}
                  onChange={() => handleShowInactiveChange(!showInactive)}
                />
                <span>{t("showInactive")}</span>
              </div>
            </div>
          </div>
          <p className={styles.selectorHint}>
            {t(
              isReadOnly
                ? "selectMerchantPriorityListToView"
                : "selectMerchantPriorityList",
            )}
          </p>
        </section>

        {isLoadingShopList && (
          <div role="status" aria-label={t("fulfillmentPriorityLoading")}>
            <Loading />
          </div>
        )}

        {!isLoadingShopList && loadError && (
          <div className={styles.errorState} role="alert">
            <p>{t("fulfillmentPriorityLoadError")}</p>
            <button
              className={styles.retryButton}
              type="button"
              onClick={() =>
                merchantCode
                  ? loadShops(merchantCode, showInactive)
                  : loadMerchants()
              }
            >
              {t("refreshPage")}
            </button>
          </div>
        )}

        {!isLoadingShopList && !loadError && shops.length === 0 && (
          <NoDataAvailableImage />
        )}

        {hasVisibleShopList && isReadOnly && (
          <div className={styles.shopList}>
            {shops.map((shop) => (
              <ShopCard key={getFulfillmentPriorityShopKey(shop)} shop={shop} />
            ))}
          </div>
        )}

        {hasVisibleShopList && !isReadOnly && (
          <>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={shopKeys}
                strategy={verticalListSortingStrategy}
              >
                <div
                  className={styles.shopList}
                  aria-busy={saving || filterLoading}
                >
                  {shops.map((shop) => (
                    <SortableShopCard
                      key={getFulfillmentPriorityShopKey(shop)}
                      shop={shop}
                      disabled={saving || filterLoading}
                      onStatusChange={handleStatusChange}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            <p className={styles.helperText}>
              {t("fulfillmentPriorityDragHelp")}
            </p>

            <div className={styles.actions}>
              <Button
                type="button"
                title={t("cancel")}
                variant="ghost-gray"
                disabled={!hasPendingChanges || saving || filterLoading}
                onClick={handleReset}
              />
              <Button
                type="button"
                title={t("save")}
                isLoading={saving}
                disabled={!hasPendingChanges || saving || filterLoading}
                onClick={handleSave}
              />
            </div>
          </>
        )}
      </section>

      <ConfirmationModal
        opened={Boolean(pendingMerchantCode) || pendingShowInactive !== null}
        title={t("unsavedChangesTitle")}
        description={t("unsavedChangesConfirmation")}
        variant="delete"
        closeModal={() => {
          setPendingMerchantCode("");
          setPendingShowInactive(null);
        }}
        confirmFunction={confirmPendingChange}
      />
    </main>
  );
}
